import _ from "lodash";
import ability from "@/services/ability";

import api from "@/api/cloud";

const residentPersonalInfoFields = [
	"firstName",
	"lastName",
	"address",
	"building",
	"primaryPhone",
	"secondaryPhone",
	"email",
	"birthdate",
	"gender",
];
const staffPersonalInfoFields = [
	"firstName",
	"lastName",
	"email",
	"jobTitle",
	"primaryPhone",
	"secondaryPhone",
];
const contactPersonalInfoFields = [
	"firstName",
	"lastName",
	"relationship",
	"email",
	"primaryPhone",
];
const moreInfoFields = ["moveInDate", "residenceType", "careSetting"];
const preferencesFields = ["checkin"];


const state = () => ({
	editable: false,
	loading: false,
	userType: "",
	profileImage: "",
	userCommunities: [],
	userSystems: [],

	data: {},
	updated: {},

	updatedProperties: {},

	// Raw SSO Data
	ssoRoles: [],
	ssoProperties: [],
	ssoAssociations: [],
});

const getters = {
	// control data
	canEdit: state => state.editable,
	advancedEdit:
		(state, g, rs, rootGetters) => state.editable && ability.can("edit", "Users"),
	loading: state => state.loading,
	type: state => state.userType,
	community: state => state.userCommunities[0],
	data: state => state.data,

	profileImage: state => state.profileImage,

	// individual form data fields
	residentPersonalInfo: state => _.pick(state.data, residentPersonalInfoFields),
	staffPersonalInfo: state => _.pick(state.data, staffPersonalInfoFields),
	contactPersonalInfo: state => _.pick(state.data, contactPersonalInfoFields),
	moreInfo: state => _.pick(state.data, moreInfoFields),
	preferences: state => _.pick(state.data, preferencesFields),

	updated: state => state.updated,
	updatedProperties: state => state.updatedProperties,

	// SSO Data
	roles: state => state.ssoRoles,
	properties: state => state.ssoProperties,
	associations: state => state.ssoAssociations,
	communities: state => state.userCommunities,
	systems: state => state.userSystems,
};

const mutations = {
	SET_EDITABLE(state, editable) {
		state.editable = editable;
	},
	SET_LOADING(state, isLoading) {
		state.loading = isLoading;
	},
	SET_USER_TYPE(state, type) {
		state.userType = type;
	},
	SET_USER_COMMUNITIES(state, communities) {
		state.userCommunities = communities;
	},
	SET_USER_SYSTEMS(state, systems) {
		state.userSystems = systems;
	},

	SET_UPDATED_DATA(state, data = ["", ""]) {
		state.updated[data[0]] = data[1];
	},
	SET_DATA(state, data = {}) {
		state.data = data;
	},

	SET_PROFILE_IMAGE(state, image) {
		state.profileImage = image;
	},

	SET_SSO_ROLES(state, roles) {
		state.ssoRoles = roles;
	},
	SET_SSO_PROPERTIES(state, properties) {
		state.ssoProperties = properties;
	},
	SET_UPDATED_SSO_PROPERTY(state, [key, value] = ["", ""]) {
		state.updatedProperties[key] = value;
	},
	DEL_UPDATED_SSO_PROPERTY(state, key) {
		delete state.updatedProperties[key];
	},
	SET_SSO_ASSOCIATIONS(state, associations) {
		state.ssoAssociations = associations;
	},

	RESET_UPDATED(state) {
		state.updated = {};
		state.updatedProperties = {};
	},
	RESET(s) {
		Object.assign(s, state());
	},
};

const actions = {
	async getUser({ commit, dispatch, getters }, id) {

		const user = await api.getUser(id);
		const { systems, communities } = user;


		commit("SET_USER_TYPE", user._type);
		commit("SET_PROFILE_IMAGE", user.photo);
		commit("SET_USER_COMMUNITIES", communities);
		commit("SET_USER_SYSTEMS", systems);

		commit("SET_DATA", user);

		commit("SET_SSO_ASSOCIATIONS", communities);
		commit("SET_SSO_PROPERTIES", user);
		commit("SET_SSO_ROLES", user._roles);
	},

	hasChanged({ getters }) {
		const totalItemsChanged = _.size(getters.updated) + _.size(getters.updatedProperties);
		return totalItemsChanged > 0;
	},

	async handleUpdateUser({ commit, dispatch, getters }, id) {
		commit("SET_LOADING", true);
		let responses = [];
		const result = {};
		try {

			if (Object.keys(getters.updated).length) {
				responses.push(await dispatch("updateUser", id));
			}
			if (Object.keys(getters.updatedProperties).length) {
				responses.push(await dispatch("updateUserProperties", id));
			}

			if (responses.length) {
				await dispatch("getUser", id);
			}

			commit("SET_EDITABLE", false);
		} catch (err) {
			result.error = err.message;
		}
		commit("SET_LOADING", false);
		return result;
	},



	async changeUserAssociationRole({ dispatch }, { method, userId, itemId = "" }) {
		const acceptedMethods = ["revokeUserRole", "addUserRole", "addAssociationUser", "removeAssociationUser"];

		if (!acceptedMethods.includes(method)) return;
		if (!Array.isArray(itemId)) itemId = [itemId];

		let results = [];

		for(const id of itemId)
		{
			let res;
			switch(method)
			{
				case 'addUserRole':
					res = await api.addUserRole(userId, id);
					break;
				case 'revokeUserRole':
					res = await api.revokeUserRole(userId, id);
					break;
				case 'addAssociationUser':
					res = await api.addUserSystem(userId, id);
					break;
				case 'removeAssociationUser':
					res = await api.removeUserSystem(userId, id);
					break;
			}
			results.push(res);
		}

		await dispatch("getUser", userId);

		return results;
	},
};

export default {
	namespaced: true,
	state,
	getters,
	mutations,
	actions,
};
